import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Loader2, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errorMessage } from "@/lib/api";
import { generatePassword } from "@/lib/password";

const empty = () => ({ name: "", email: "", password: generatePassword() });

const MemberRow = ({ m, onRemove }) => (
  <li className="flex items-center gap-3 rounded-lg border border-slate-700/60 bg-slate-800/60 px-3 py-2" data-testid={`team-member-${m.id}`}>
    <div className="min-w-0 flex-1">
      <div className="truncate text-sm font-semibold">{m.name || m.email}</div>
      <div className="truncate text-xs text-slate-400">{m.email}</div>
    </div>
    <span className="rounded-full border border-slate-600 px-2 py-0.5 text-[11px] text-slate-300">{m.tenant_role === "owner" ? "Responsável" : "Usuário"}</span>
    {m.tenant_role !== "owner" && (
      <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-400" onClick={() => onRemove(m)} data-testid={`team-remove-${m.id}`} aria-label="Remover"><Trash2 className="h-4 w-4" /></Button>
    )}
  </li>
);

export const TeamDialog = ({ open, onClose }) => {
  const [members, setMembers] = useState([]);
  const [f, setF] = useState(empty());
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/team").then((r) => setMembers(r.data)).catch((e) => toast.error(errorMessage(e))), []);
  useEffect(() => { if (open) { load(); setF(empty()); } }, [open, load]);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/team", f);
      toast.success(`Usuário criado. Login: ${f.email.trim().toLowerCase()} · Senha: ${f.password}`, { duration: 12000 });
      setF(empty());
      load();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (m) => {
    try {
      await api.delete(`/team/${m.id}`);
      toast.success("Usuário removido.");
      load();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto border-slate-700 bg-slate-900 sm:max-w-lg" data-testid="team-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold">Equipe</DialogTitle>
          <DialogDescription>Todos os usuários veem e editam os mesmos dados da empresa.</DialogDescription>
        </DialogHeader>
        <ul className="space-y-2" data-testid="team-list">{members.map((m) => <MemberRow key={m.id} m={m} onRemove={remove} />)}</ul>
        <form onSubmit={add} className="space-y-3 rounded-xl border border-slate-700 bg-slate-950 p-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Adicionar usuário</div>
          <Input required placeholder="Nome" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="bg-slate-900" data-testid="team-name-input" />
          <Input required type="email" placeholder="E-mail de login" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="bg-slate-900" data-testid="team-email-input" />
          <div className="flex gap-2">
            <Input required minLength={6} placeholder="Senha" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="bg-slate-900 font-mono" data-testid="team-password-input" />
            <Button type="button" variant="outline" className="border-slate-700 bg-transparent" onClick={() => setF({ ...f, password: generatePassword() })} data-testid="team-generate-password" title="Gerar senha"><KeyRound className="h-4 w-4" /></Button>
          </div>
          <Button type="submit" disabled={saving} className="w-full rounded-full font-semibold" data-testid="team-add-button">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Criar usuário
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
